"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const ProjectContext_1 = require("../contexts/ProjectContext");
const websocket_1 = require("../services/websocket");
const lucide_react_1 = require("lucide-react");
const api_1 = require("../services/api");
const ProjectListPanel = () => {
    const { state, loadProjects, loadProject, createProject, updateProjectSpellCheck } = (0, ProjectContext_1.useProjectContext)();
    const [showNewProjectModal, setShowNewProjectModal] = (0, react_1.useState)(false);
    const [newProjectUrl, setNewProjectUrl] = (0, react_1.useState)('');
    const [isCreating, setIsCreating] = (0, react_1.useState)(false);
    const [progressMessages, setProgressMessages] = (0, react_1.useState)(new Map());
    const [actionLoading, setActionLoading] = (0, react_1.useState)(new Map());
    const [availableLanguages, setAvailableLanguages] = (0, react_1.useState)(new Map());
    const [showLanguageModal, setShowLanguageModal] = (0, react_1.useState)(null);
    const [showOnlyCzech, setShowOnlyCzech] = (0, react_1.useState)(true);
    // WebSocket connection for the currently selected project
    const { isConnected, lastMessage } = (0, websocket_1.useWebSocket)(state.currentProject?.project_id || null);
    (0, react_1.useEffect)(() => {
        // Listen for menu events
        if (window.electronAPI) {
            window.electronAPI.onMenuNewProject(() => {
                setShowNewProjectModal(true);
            });
            return () => {
                window.electronAPI?.removeAllListeners('menu-new-project');
            };
        }
    }, []);
    // Handle WebSocket messages
    (0, react_1.useEffect)(() => {
        if (lastMessage) {
            if (lastMessage.type === 'progress') {
                setProgressMessages(prev => new Map(prev.set(lastMessage.project_id, lastMessage.message)));
            }
            else if (lastMessage.type === 'status') {
                // Refresh projects when status changes
                loadProjects();
                // Clear progress message for this project
                setProgressMessages(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(lastMessage.project_id);
                    return newMap;
                });
            }
        }
    }, [lastMessage, loadProjects]);
    // Handlery pro akce
    const setProjectActionLoading = (projectId, action) => {
        setActionLoading(prev => new Map(prev.set(projectId, action)));
    };
    const clearProjectActionLoading = (projectId) => {
        setActionLoading(prev => {
            const newMap = new Map(prev);
            newMap.delete(projectId);
            return newMap;
        });
    };
    const handleDeleteProject = async (projectId) => {
        if (!confirm('Opravdu chcete smazat tento projekt?'))
            return;
        try {
            setProjectActionLoading(projectId, 'deleting');
            await api_1.projectsApi.deleteProject(projectId);
            await loadProjects();
        }
        catch (error) {
            console.error('Failed to delete project:', error);
            alert('Nepodařilo se smazat projekt');
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleExtractSubtitles = async (projectId) => {
        try {
            setProjectActionLoading(projectId, 'loading-languages');
            const languagesData = await api_1.projectsApi.getAvailableSubtitles(projectId);
            if (languagesData.available_languages && languagesData.available_languages.length > 0) {
                setAvailableLanguages(prev => new Map(prev.set(projectId, languagesData.available_languages)));
                setShowLanguageModal(projectId);
            }
            else {
                alert('⚠️ Pro toto video nejsou dostupné žádné titulky');
            }
        }
        catch (error) {
            console.error('Failed to get available languages:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Nepodařilo se načíst dostupné jazyky';
            if (error.response?.status === 403 || errorMessage.includes('soukromé') || errorMessage.includes('dostupné')) {
                alert('❌ Video není dostupné (soukromé nebo smazané)');
                await loadProjects(); // Refresh to show error status
            }
            else {
                alert(`❌ ${errorMessage}`);
            }
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    // Funkce pro filtrování českých titulků
    const getFilteredLanguages = (languages) => {
        if (!showOnlyCzech) {
            return languages;
        }
        // Filtruj pouze české titulky (cs, cs-orig, česká, čeština, atd.)
        return languages.filter(lang => {
            const code = lang.code?.toLowerCase() || '';
            const name = lang.name?.toLowerCase() || '';
            return code.startsWith('cs') ||
                name.includes('česk') ||
                name.includes('čeština') ||
                code === 'cz';
        });
    };
    const extractSubtitlesWithLanguage = async (projectId, language) => {
        try {
            setProjectActionLoading(projectId, 'extracting-subtitles');
            setShowLanguageModal(null); // Zavři modal okamžitě
            const result = await api_1.projectsApi.extractSubtitles(projectId, language);
            // Zobrazí výsledek
            if (result.segments_count > 0) {
                alert(`✅ ${result.message}`);
                await loadProjects();
                if (state.currentProject?.project_id === projectId) {
                    await loadProject(projectId);
                }
            }
            else {
                alert(`⚠️ ${result.message}`);
            }
        }
        catch (error) {
            console.error('Failed to extract subtitles:', error);
            const errorMessage = error.response?.data?.detail || 'Nepodařilo se extrahovat titulky';
            alert(`❌ ${errorMessage}`);
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleDownloadAudio = async (projectId) => {
        try {
            setProjectActionLoading(projectId, 'downloading-audio');
            const result = await api_1.projectsApi.downloadAudio(projectId);
            alert(`✅ ${result.message}`);
            await loadProjects();
        }
        catch (error) {
            console.error('Failed to download audio:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Nepodařilo se stáhnout audio';
            // Zobrazíme detailní chybovou zprávu s možností opakování
            const retry = confirm(`❌ Chyba při stahování audio:\n${errorMessage}\n\nChcete to zkusit znovu?`);
            if (retry) {
                // Opakujeme operaci
                setTimeout(() => handleDownloadAudio(projectId), 1000);
                return;
            }
            // Refresh projektů - projekt zůstává v původním stavu (nepoškozený)
            await loadProjects();
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleWhisperTranscribe = async (projectId) => {
        try {
            setProjectActionLoading(projectId, 'transcribing');
            const result = await api_1.projectsApi.transcribeWithWhisper(projectId);
            alert(`✅ ${result.message}`);
            await loadProjects();
            if (state.currentProject?.project_id === projectId) {
                await loadProject(projectId);
            }
        }
        catch (error) {
            console.error('Failed to transcribe with Whisper:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Nepodařilo se provést Whisper transkripci';
            // Zobrazíme detailní chybovou zprávu s možností opakování
            const retry = confirm(`❌ Chyba při Whisper transkripci:\n${errorMessage}\n\nChcete to zkusit znovu?`);
            if (retry) {
                // Opakujeme operaci
                setTimeout(() => handleWhisperTranscribe(projectId), 1000);
                return;
            }
            // Refresh projektů - projekt zůstává v původním stavu (nepoškozený)
            await loadProjects();
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleAICorrection = async (projectId) => {
        try {
            setProjectActionLoading(projectId, 'correcting');
            const result = await api_1.projectsApi.correctWithAI(projectId);
            alert(`✅ ${result.message}`);
            await loadProjects();
            if (state.currentProject?.project_id === projectId) {
                await loadProject(projectId);
            }
        }
        catch (error) {
            console.error('Failed to correct with AI:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Nepodařilo se provést AI korekci';
            // Zobrazíme detailní chybovou zprávu s možností opakování
            const retry = confirm(`❌ Chyba při AI korekci:\n${errorMessage}\n\nChcete to zkusit znovu?`);
            if (retry) {
                // Opakujeme operaci
                setTimeout(() => handleAICorrection(projectId), 1000);
                return;
            }
            // Refresh projektů - projekt zůstává v původním stavu (nepoškozený)
            await loadProjects();
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleResetProject = async (projectId) => {
        const confirmed = confirm('⚠️ Opravdu chcete resetovat projekt?\n\nTato akce smaže všechny segmenty a dočasné soubory. Projekt se vrátí do původního stavu.');
        if (!confirmed)
            return;
        try {
            setProjectActionLoading(projectId, 'resetting');
            const result = await api_1.projectsApi.resetProject(projectId);
            alert(`✅ ${result.message}`);
            await loadProjects();
            // Pokud je resetovaný projekt aktuálně otevřený, refresh projektů ho automaticky aktualizuje
        }
        catch (error) {
            console.error('Failed to reset project:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Nepodařilo se resetovat projekt';
            alert(`❌ ${errorMessage}`);
        }
        finally {
            clearProjectActionLoading(projectId);
        }
    };
    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!newProjectUrl.trim())
            return;
        setIsCreating(true);
        try {
            await createProject(newProjectUrl.trim());
            setNewProjectUrl('');
            setShowNewProjectModal(false);
        }
        catch (error) {
            console.error('Failed to create project:', error);
        }
        finally {
            setIsCreating(false);
        }
    };
    const getStatusColor = (status) => {
        switch (status) {
            case 'loaded':
                return 'text-blue-600 bg-blue-50';
            case 'processing':
                return 'text-blue-600 bg-blue-50';
            case 'subtitles_extracted':
                return 'text-purple-600 bg-purple-50';
            case 'audio_downloaded':
                return 'text-green-600 bg-green-50';
            case 'whisper_transcribed':
                return 'text-indigo-600 bg-indigo-50';
            case 'ai_corrected':
                return 'text-teal-600 bg-teal-50';
            case 'needs_review':
                return 'text-orange-600 bg-orange-50';
            case 'completed':
                return 'text-green-600 bg-green-50';
            case 'error':
                return 'text-red-600 bg-red-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };
    const getStatusText = (status) => {
        switch (status) {
            case 'loaded':
                return 'Načteno';
            case 'processing':
                return 'Zpracovává se...';
            case 'subtitles_extracted':
                return 'Titulky extrahovány';
            case 'audio_downloaded':
                return 'Audio staženo';
            case 'whisper_transcribed':
                return 'Whisper dokončen';
            case 'ai_corrected':
                return 'AI korekce dokončena';
            case 'needs_review':
                return 'Vyžaduje kontrolu';
            case 'completed':
                return 'Dokončeno';
            case 'error':
                return 'Chyba';
            default:
                return 'Neznámý stav';
        }
    };
    const getActionLoadingText = (action) => {
        switch (action) {
            case 'extract-subtitles':
                return 'Načítání titulků...';
            case 'download-audio':
                return 'Stahování audio...';
            case 'whisper-transcribe':
                return 'Whisper transkripce...';
            case 'ai-correction':
                return 'AI korekce...';
            case 'delete':
                return 'Mazání...';
            default:
                return 'Zpracovává se...';
        }
    };
    // Helper funkce pro validaci dostupnosti akcí
    const isActionAvailable = (action, project) => {
        const status = project.status;
        switch (action) {
            case 'extract-subtitles':
                // Titulky lze načíst vždy (pokud není chyba)
                return status !== 'error' && status !== 'creating';
            case 'download-audio':
                // Audio lze stáhnout vždy (pokud není chyba)
                return status !== 'error' && status !== 'creating';
            case 'whisper-transcribe':
                // Whisper vyžaduje stažené audio
                return status === 'audio_downloaded' ||
                    status === 'whisper_transcribed' ||
                    status === 'ai_corrected';
            case 'ai-correction':
                // AI korekce vyžaduje existující segmenty (z titulků nebo Whisper)
                return status === 'subtitles_extracted' ||
                    status === 'whisper_transcribed' ||
                    status === 'ai_corrected';
            case 'delete':
                // Smazat lze vždy
                return true;
            default:
                return false;
        }
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "h-full flex flex-col", children: [(0, jsx_runtime_1.jsx)("div", { className: "p-4 border-b border-gray-200", children: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center justify-between", children: [(0, jsx_runtime_1.jsx)("h2", { className: "text-lg font-semibold text-gray-900", children: "Projekty" }), (0, jsx_runtime_1.jsx)("button", { onClick: () => setShowNewProjectModal(true), className: "p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors", title: "Nov\u00FD projekt", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Plus, { className: "w-5 h-5" }) })] }) }), (0, jsx_runtime_1.jsx)("div", { className: "flex-1 overflow-y-auto", children: state.loading && state.projects.length === 0 ? ((0, jsx_runtime_1.jsx)("div", { className: "flex items-center justify-center h-32", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-6 h-6 animate-spin text-gray-400" }) })) : state.error ? ((0, jsx_runtime_1.jsxs)("div", { className: "p-4 text-center", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-8 h-8 text-red-500 mx-auto mb-2" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-600", children: state.error })] })) : state.projects.length === 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: "p-4 text-center", children: [(0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-500 mb-4", children: "\u017D\u00E1dn\u00E9 projekty" }), (0, jsx_runtime_1.jsx)("button", { onClick: () => setShowNewProjectModal(true), className: "text-sm text-blue-600 hover:text-blue-700", children: "Vytvo\u0159it prvn\u00ED projekt" })] })) : ((0, jsx_runtime_1.jsx)("div", { className: "divide-y divide-gray-100", children: state.projects.map((project) => ((0, jsx_runtime_1.jsx)("div", { className: `p-4 cursor-pointer hover:bg-gray-50 transition-colors ${state.currentProject?.project_id === project.project_id
                            ? 'bg-blue-50 border-r-2 border-blue-600'
                            : ''}`, onClick: () => loadProject(project.project_id), children: (0, jsx_runtime_1.jsxs)("div", { className: "space-y-2", children: [(0, jsx_runtime_1.jsx)("h3", { className: "font-medium text-gray-900 truncate", children: project.video_title || 'Načítá se...' }), (0, jsx_runtime_1.jsx)("div", { className: "flex items-center space-x-2", children: (0, jsx_runtime_1.jsx)("span", { className: `px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`, children: getStatusText(project.status) }) }), (0, jsx_runtime_1.jsx)("p", { className: "text-xs text-gray-500", children: project.created_at ?
                                        new Date(project.created_at).toLocaleDateString('cs-CZ') :
                                        'Neznámé datum' }), progressMessages.has(project.project_id) && ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2 text-xs text-blue-600", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-3 h-3 animate-spin" }), (0, jsx_runtime_1.jsx)("span", { children: progressMessages.get(project.project_id) })] })), actionLoading.has(project.project_id) && ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2 text-xs text-orange-600", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-3 h-3 animate-spin" }), (0, jsx_runtime_1.jsx)("span", { children: getActionLoadingText(actionLoading.get(project.project_id)) })] })), project.status === 'error' && project.error_message && ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center space-x-2 text-xs text-red-600", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.AlertCircle, { className: "w-3 h-3" }), (0, jsx_runtime_1.jsx)("span", { children: project.error_message })] })), (0, jsx_runtime_1.jsx)("div", { className: "flex items-center justify-end mt-2", children: (0, jsx_runtime_1.jsx)("button", { onClick: (e) => {
                                            e.stopPropagation();
                                            handleDeleteProject(project.project_id);
                                        }, disabled: actionLoading.has(project.project_id), className: "p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50", title: "Smazat projekt", children: (0, jsx_runtime_1.jsx)(lucide_react_1.Trash2, { className: "w-4 h-4" }) }) })] }) }, project.project_id))) })) }), showNewProjectModal && ((0, jsx_runtime_1.jsx)("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "bg-white rounded-lg p-6 w-96 max-w-full mx-4", children: [(0, jsx_runtime_1.jsx)("h3", { className: "text-lg font-semibold mb-4", children: "Nov\u00FD projekt" }), (0, jsx_runtime_1.jsx)("form", { onSubmit: handleCreateProject, children: (0, jsx_runtime_1.jsxs)("div", { className: "space-y-4", children: [(0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("label", { className: "block text-sm font-medium text-gray-700 mb-1", children: "YouTube URL" }), (0, jsx_runtime_1.jsx)("input", { type: "url", value: newProjectUrl, onChange: (e) => setNewProjectUrl(e.target.value), placeholder: "https://www.youtube.com/watch?v=...", className: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", required: true, disabled: isCreating })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex justify-end space-x-3", children: [(0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                                                    setShowNewProjectModal(false);
                                                    setNewProjectUrl('');
                                                }, className: "px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200", disabled: isCreating, children: "Zru\u0161it" }), (0, jsx_runtime_1.jsx)("button", { type: "submit", className: "px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50", disabled: isCreating || !newProjectUrl.trim(), children: isCreating ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Loader2, { className: "w-4 h-4 animate-spin inline mr-2" }), "Vytv\u00E1\u0159\u00ED se..."] })) : ('Vytvořit') })] })] }) })] }) })), showLanguageModal && ((0, jsx_runtime_1.jsx)("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: (0, jsx_runtime_1.jsxs)("div", { className: "bg-white rounded-lg p-6 w-96 max-w-full mx-4", children: [(0, jsx_runtime_1.jsx)("h3", { className: "text-lg font-semibold mb-4", children: "Vyberte jazyk titulk\u016F" }), (0, jsx_runtime_1.jsxs)("div", { className: "mb-4 p-3 bg-gray-50 rounded-lg", children: [(0, jsx_runtime_1.jsxs)("label", { className: "flex items-center space-x-2 cursor-pointer", children: [(0, jsx_runtime_1.jsx)("input", { type: "checkbox", checked: showOnlyCzech, onChange: (e) => setShowOnlyCzech(e.target.checked), className: "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2" }), (0, jsx_runtime_1.jsx)("span", { className: "text-sm font-medium text-gray-700", children: "Jen \u010Desk\u00E9" })] }), (0, jsx_runtime_1.jsx)("p", { className: "text-xs text-gray-500 mt-1", children: "Zobrazit pouze \u010Desk\u00E9 titulky (cs, cs-orig, \u010De\u0161tina)" })] }), (() => {
                            const allLanguages = availableLanguages.get(showLanguageModal) || [];
                            const filteredLanguages = getFilteredLanguages(allLanguages);
                            if (allLanguages.length === 0) {
                                return ((0, jsx_runtime_1.jsxs)("div", { className: "text-center py-8", children: [(0, jsx_runtime_1.jsx)("div", { className: "text-gray-500 mb-4", children: "\u017D\u00E1dn\u00E9 titulky nejsou dostupn\u00E9" }), (0, jsx_runtime_1.jsx)("button", { onClick: () => setShowLanguageModal(null), className: "px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700", children: "Zav\u0159\u00EDt" })] }));
                            }
                            if (filteredLanguages.length === 0 && showOnlyCzech) {
                                return ((0, jsx_runtime_1.jsxs)("div", { className: "text-center py-8", children: [(0, jsx_runtime_1.jsx)("div", { className: "text-gray-500 mb-4", children: "\u017D\u00E1dn\u00E9 \u010Desk\u00E9 titulky nejsou dostupn\u00E9" }), (0, jsx_runtime_1.jsx)("p", { className: "text-sm text-gray-400 mb-4", children: "Zkuste od\u0161krtnout \"Jen \u010Desk\u00E9\" pro zobrazen\u00ED v\u0161ech jazyk\u016F" }), (0, jsx_runtime_1.jsx)("button", { onClick: () => setShowLanguageModal(null), className: "px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700", children: "Zav\u0159\u00EDt" })] }));
                            }
                            return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "space-y-2 max-h-60 overflow-y-auto", children: filteredLanguages.map((lang) => ((0, jsx_runtime_1.jsxs)("button", { onClick: () => extractSubtitlesWithLanguage(showLanguageModal, lang.code), disabled: actionLoading.has(showLanguageModal), className: "w-full text-left p-3 border rounded hover:bg-gray-50 flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed", children: [(0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("div", { className: "font-medium", children: lang.name }), (0, jsx_runtime_1.jsxs)("div", { className: "text-sm text-gray-500", children: [lang.code, " \u2022 ", lang.type, lang.type === 'manuální' && (0, jsx_runtime_1.jsx)("span", { className: "ml-1 text-green-600", children: "\u2713" })] })] }), (0, jsx_runtime_1.jsx)(lucide_react_1.Languages, { className: "w-4 h-4 text-gray-400" })] }, lang.code))) }), (0, jsx_runtime_1.jsxs)("div", { className: "mt-3 text-xs text-gray-500 text-center", children: ["Zobrazeno ", filteredLanguages.length, " z ", allLanguages.length, " dostupn\u00FDch jazyk\u016F"] }), (0, jsx_runtime_1.jsx)("div", { className: "flex justify-end space-x-3 mt-4", children: (0, jsx_runtime_1.jsx)("button", { onClick: () => setShowLanguageModal(null), disabled: actionLoading.has(showLanguageModal), className: "px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50", children: "Zru\u0161it" }) })] }));
                        })()] }) }))] }));
};
// Pomocné funkce
const getActionLoadingText = (action) => {
    const texts = {
        'deleting': 'Mazání...',
        'loading-languages': 'Načítání jazyků...',
        'extracting-subtitles': 'Extrakce titulků...',
        'downloading-audio': 'Stahování audio...',
        'transcribing': 'Whisper transkripce...',
        'correcting': 'AI korekce...'
    };
    return texts[action] || 'Zpracovává se...';
};
exports.default = ProjectListPanel;
